import { type FC, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Card } from 'Card';
import { Text } from 'Text';

/*
    Shared chrome for the timer, schedule and score boxes. The divider shows only at md and up. padding={0} lets
    the divider span the full width.
*/

type Props = {
    caption: string;
    children: ReactNode;

    // The timer uses wider sides: padding 12px 40px 15px rather than 12px 15px 15px.
    horizontalPadding?: 'default' | 'wide';

    // The score box shows its divider at every width; the other two only at md and up.
    alwaysShowDivider?: boolean;

    // The score box's caption was a .header rather than a .hr-caption, and that class uppercases it.
    uppercaseCaption?: boolean;

    /*
        Whether the body is centred. .timer-box and .exam-score-box centre theirs; .exam-schedule-box
        leaves it at the start. The caption is centred in all three, because it was a .hr-caption and
        that global class centres it.

        This has to be applied to our own elements rather than to Card, because HeroUI's CardBody sets
        text-left and that beats anything inherited from Card's base. text-left is also physical, so the
        uncentred case sets text-start explicitly instead of inheriting it.
    */
    centerBody?: boolean;
    className?: string;
};

export const ExamInfoBox: FC<Props> = ({
    caption,
    children,
    horizontalPadding = 'default',
    alwaysShowDivider = false,
    uppercaseCaption = false,
    centerBody = false,
    className,
}) => {
    const sides = horizontalPadding === 'wide' ? 'px-10' : 'px-3.5';

    return (
        <Card width="full" radius={5} padding={0} className={className}>
            <div className={twMerge('pt-3 text-center', sides)}>
                <Text
                    as="h3"
                    uppercase={uppercaseCaption}
                    className="m-0 text-lg font-normal leading-[25px] text-black"
                >
                    {caption}
                </Text>
            </div>
            <hr
                className={twMerge(
                    'mt-3 border-0 border-t border-slate-grey-200',
                    !alwaysShowDivider && 'max-md:hidden',
                )}
            />
            {/* text-start rather than relying on inheritance: CardBody sets the physical text-left, which
                does not flip for a right-to-left locale. */}
            <div
                className={twMerge('pb-3.5 pt-3 text-xs text-black', sides, centerBody ? 'text-center' : 'text-start')}
            >
                {children}
            </div>
        </Card>
    );
};
